import React, {useMemo, useState} from 'react';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';

const flattenVersion = (version) => {
    const lines = [];
    lines.push(`formula: ${version.formula} (${version.formulaAmount ?? '—'})`);
    lines.push(`dailyCap: ${version.dailyCap}`);
    lines.push(`monthlyCap: ${version.monthlyCap}`);
    if (version.conditions) {
        lines.push(`conditions: ${JSON.stringify(version.conditions)}`);
    }
    return lines;
};

const computeDiff = (prevLines, currLines) => {
    const added = currLines.filter((l) => !prevLines.includes(l));
    const removed = prevLines.filter((l) => !currLines.includes(l));
    const changed = currLines.filter((l) => prevLines.includes(l) === false && !added.includes(l));
    return {added, removed, changed: changed.length ? changed : added.filter((l) => removed.length > 0)};
};

const HistoryDialog = ({open, rule, onClose}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.rules.history'});
    const [selectedVer, setSelectedVer] = useState(null);

    const versions = rule?.versions ?? [];
    const activeVer = selectedVer ?? versions[versions.length - 1]?.ver;

    const diff = useMemo(() => {
        const idx = versions.findIndex((v) => v.ver === activeVer);
        if (idx <= 0) {
            const curr = flattenVersion(versions[idx] ?? {});
            return {added: curr, removed: [], changed: [], isCreated: true};
        }
        const prev = flattenVersion(versions[idx - 1]);
        const curr = flattenVersion(versions[idx]);
        return {...computeDiff(prev, curr), isCreated: false};
    }, [activeVer, versions]);

    /**
     * Resets selected version when dialog opens.
     */
    React.useEffect(() => {
        if (open && rule?.versions?.length) {
            setSelectedVer(rule.versions[rule.versions.length - 1].ver);
        }
    }, [open, rule]);

    if (!rule) {
        return null;
    }

    return (
        <Dialog
            open={open}
            setOpen={(v) => {
                if (!v) {
                    onClose?.();
                }
            }}
            maxWidth="md"
        >
            <DialogTitle>{t('title', {name: rule.name})}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{mt: 1}}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('selectVersion')}</InputLabel>
                        <Select
                            label={t('selectVersion')}
                            value={activeVer ?? ''}
                            onChange={(e) => setSelectedVer(e.target.value)}
                        >
                            {versions.map((v) => (
                                <MenuItem key={v.ver} value={v.ver}>
                                    {t('versionOption', {ver: v.ver, date: v.createdAt})}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {diff.isCreated ? (
                        <Typography variant="body2" color="text.secondary">{t('createdVersion')}</Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">{t('diffVsPrevious')}</Typography>
                    )}

                    {diff.added.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" color="success.main">{t('added')}</Typography>
                            {diff.added.map((line) => (
                                <Typography key={line} variant="body2" sx={{fontFamily: 'monospace'}}>+ {line}</Typography>
                            ))}
                        </Box>
                    )}
                    {diff.removed.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" color="error.main">{t('removed')}</Typography>
                            {diff.removed.map((line) => (
                                <Typography key={line} variant="body2" sx={{fontFamily: 'monospace'}}>- {line}</Typography>
                            ))}
                        </Box>
                    )}
                    {diff.changed.length > 0 && !diff.isCreated && diff.added.length === 0 && diff.removed.length === 0 && (
                        <Box>
                            <Typography variant="subtitle2" color="warning.main">{t('changed')}</Typography>
                            {diff.changed.map((line) => (
                                <Typography key={line} variant="body2" sx={{fontFamily: 'monospace'}}>~ {line}</Typography>
                            ))}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{p: 0, m: 0, display: 'flex', justifyContent: 'space-between'}}>
                <Button variant="dialog" color="primary" borderRadius="0 8px 0 8px" onClick={onClose}>
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HistoryDialog;
